/**
 * Trang /models — registry model ONNX (muc 12 dac ta).
 *
 * Man hinh nay lam ba viec, va thu tu quan trong cua chung dung nhu thu tu nay:
 *
 *  1. NOI RO MODEL NAO DANG PHUC VU, va model nao KHONG duoc phep phuc vu vi sao.
 *     Mot registry chi liet ke ten file thi vo dung; cai nguoi van hanh can biet la
 *     "cai dang chay co du tot khong, va neu doi thi doi ve dau".
 *  2. CHO PHEP QUAY VE VERSION CU trong mot cu bam. Train lai co the lam model te di —
 *     duong lui phai nhanh hon duong tien.
 *  3. DUA SAN CAC LENH PYTHON de train. Training chay NGOAI Node (muc 4: `onnxruntime-node`
 *     lo inference, PyTorch lo training), nen nguoi dung phai tu chay tay o terminal.
 *     Bat ho di tra tai lieu de go lai duong dan la cach chac chan nhat de ho khong train.
 *
 * Nguyen tac hien thi: KHONG BAO GIO an mot model khong dat chuan. Model duoi nguong van
 * nam trong bang de con so sanh cac lan train voi nhau — chi co nut kich hoat bi vo hieu,
 * kem ly do viet ro bang chu.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ProfileDto, SolverInfo } from '@capforge/shared';
import { adminFetch, api, errorMessage, getAuth } from '../lib/api';
import type { AdminAuth } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime, formatNumber, formatRelative, prettyJson } from '../lib/format';
import { Table } from '../components/Table';
import type { Column } from '../components/Table';
import { Badge } from '../components/Badge';
import { Modal } from '../components/Modal';
import { CopyButton } from '../components/CopyButton';
import { ErrorBox } from '../components/ErrorBox';
import { Spinner } from '../components/Spinner';

// ---------------------------------------------------------------------------
// Hop dong voi server — ban sao co chu dich
//
// Cac hang so duoi day duoc CHEP LAI tu apps/server/src/solvers/ocr/contract.ts.
// Dashboard khong import duoc tu apps/server (hai package khac nhau, va type cua server
// khong nam trong @capforge/shared), nen ban sao la khong tranh khoi.
//
// De ban sao nay khong bao gio tro thanh nguon sai lech: no CHI dung de doan truoc va
// giai thich cho nguoi dung. Nguoi quyet dinh cuoi cung van la server — bam kich hoat mot
// model khong dat chuan se nhan HTTP 409 va thong diep cua server duoc hien nguyen van.
// ---------------------------------------------------------------------------

/** Nguong valAccuracy toi thieu de duoc phuc vu (muc 9). Nguon: OCR_MIN_VAL_ACCURACY. */
const OCR_MIN_VAL_ACCURACY = 0.92;

/** Sai so goc trung binh toi da de model `rotate` duoc phuc vu (muc 9). Nguon: ROTATE_MAX_MEAN_ERROR_DEG. */
const ROTATE_MAX_MEAN_ERROR_DEG = 8;

/** Kich thuoc anh va ten tensor theo hop dong ONNX — dung de canh bao model lech chuan. */
const OCR_CONTRACT = {
  imgHeight: 32,
  imgWidth: 128,
  timeSteps: 32,
  inputName: 'image',
  outputName: 'logits',
} as const;

/** Ten model: chep lai regex cua POST /admin/models/import de bao loi ngay tai form. */
const MODEL_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * So mau tong hop mac dinh trong lenh sinh du lieu.
 *
 * 50.000 la con so o muc 15 dac ta cho moc nghiem thu Phase 2. No du lon de model qua duoc
 * nguong 92%, va tren may 4 nhan khong GPU thi van sinh xong trong khoang cho duoc.
 */
const DEFAULT_SYNTH_COUNT = 50_000;

// ---------------------------------------------------------------------------
// Kieu du lieu tra ve tu /admin/models
// ---------------------------------------------------------------------------

/**
 * Noi dung metrics.json di kem file .onnx.
 *
 * Cac truong co gia tri mac dinh o phia server luon co mat; nhom so do (valAccuracy,
 * trainSamples, ...) la tuy chon vi mot file metrics viet tay co the thieu. Doc chung
 * bang `finite()` chu khong tin thang, de mot gia tri null lot luoi khong lam vo trang.
 */
interface ModelMeta {
  // ==========================================================================
  //  Tu Phase 5, registry phuc vu NHIEU loai model, va chung do chat luong bang
  //  nhung dai luong khac han nhau: `ocr` do do chinh xac chuoi (cang CAO cang tot),
  //  `rotate` do sai so goc (cang THAP cang tot). Vi vay moi truong o day deu la TUY
  //  CHON — doc thang `meta.charset.length` tren mot model `rotate` lam vo ca trang,
  //  va do la loi chi lo ra khi co nguoi nap model rotate dau tien.
  // ==========================================================================
  charset?: string;
  imgHeight?: number;
  imgWidth?: number;
  timeSteps?: number;
  darkTextOnLight?: boolean;
  // rotate
  imgSize?: number;
  channels?: number;
  valMeanErrorDeg?: number;
  valWithin8Deg?: number;
  sourceImages?: string;
  // chung
  inputName?: string;
  outputName?: string;
  normalization?: string;
  valAccuracy?: number;
  realAccuracy?: number;
  trainSamples?: number;
  valSamples?: number;
  epochs?: number;
  trainedAt?: string;
  presets?: string[];
  notes?: string;
}

interface ModelDto {
  id: number;
  name: string;
  version: number;
  solver: string;
  /** null = model GENERIC, dung cho moi profile chua co model rieng. */
  profileKey: string | null;
  /** Tuong doi so voi CAPFORGE_DATA_DIR, kieu POSIX. */
  onnxPath: string;
  /** null khi metrics.json luu trong DB da hong — van phai hien duoc de con di sua. */
  meta: ModelMeta | null;
  active: boolean;
  createdAt: string;
  fileExists: boolean;
  fileBytes: number | null;
}

// ---------------------------------------------------------------------------
// Tien ich hien thi
// ---------------------------------------------------------------------------

function finite(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Do chinh xac hien voi HAI chu so thap phan, khong dung formatRatio (mot chu so).
 *
 * Ly do: con so nay dung de so sanh cac lan train voi nhau. 92,3% va 92,3% co the la
 * 92,25% va 92,34% — lam tron toi mot chu so thi hai lan train khac nhau nhin y het nhau,
 * dung luc dang can quyet dinh giu lan nao.
 */
function formatAccuracy(value: number | null): string {
  if (value === null) return '—';
  return `${(value * 100).toFixed(2).replace('.', ',')}%`;
}

/** Kich thuoc file .onnx. Model CRNN thuong vai MB — thay "0 B" la biet export hong. */
function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1).replace('.', ',')} KB`;
  return `${(kb / 1024).toFixed(1).replace('.', ',')} MB`;
}

/** Model phuc vu theo cap (solver, profileKey) — dung lam khoa gom nhom, giong phia server. */
function groupKey(model: ModelDto): string {
  return JSON.stringify([model.solver, model.profileKey]);
}

function profileLabel(profileKey: string | null): ReactNode {
  if (profileKey === null) {
    return (
      <span className="text-slate-500 dark:text-slate-400" title="Dùng cho mọi profile chưa có model riêng">
        generic
      </span>
    );
  }
  return <span className="cf-mono">{profileKey}</span>;
}

/**
 * Ly do KHONG duoc kich hoat, hoac null neu du dieu kien.
 * Soi guong activationBlockReason() cua apps/server/src/models/registry.ts.
 */
function activationBlock(solver: string, meta: ModelMeta | null): string | null {
  if (meta === null) {
    return 'không đọc được metrics.json của model này, nên không có bằng chứng nào cho thấy nó đủ tốt để phục vụ';
  }

  if (solver === 'rotate') {
    const err = finite(meta.valMeanErrorDeg);
    if (err === null) {
      return 'metrics.json không có trường valMeanErrorDeg — không đo được thì không kích hoạt';
    }
    // Chieu so sanh NGUOC voi `ocr`: o day so cang NHO cang tot. Chep nham chieu la loi
    // im lang hoan hao — no se cho qua dung nhung model te nhat.
    if (err > ROTATE_MAX_MEAN_ERROR_DEG) {
      return `sai so goc trung binh ${err.toFixed(2)} do, cao hon nguong ${ROTATE_MAX_MEAN_ERROR_DEG} do (muc 9 dac ta)`;
    }
    return null;
  }

  const acc = finite(meta.valAccuracy);
  if (acc === null) {
    // Thieu so do thi mac dinh la KHONG duoc phuc vu — gia dinh nguoc lai chi can dung
    // mot lan la du hong niem tin vao ca he thong.
    return 'metrics.json không có trường valAccuracy — không đo được thì không kích hoạt';
  }
  if (acc < OCR_MIN_VAL_ACCURACY) {
    return `valAccuracy ${formatAccuracy(acc)} thap hon nguong ${formatAccuracy(OCR_MIN_VAL_ACCURACY)} (muc 9 dac ta)`;
  }
  return null;
}

/**
 * So do chat luong de hien tren bang, theo dung dai luong ma solver do dung.
 *
 * Mot cot ten cung "valAccuracy" se luon trong ron voi model `rotate` — va mot o trong
 * doc ra la "chua do" chu khong phai "do bang thu khac", tuc no noi doi voi nguoi nhin.
 */
function qualityCell(model: ModelDto): { label: string; text: string; passes: boolean } | null {
  const meta = model.meta;
  if (meta === null) return null;
  if (model.solver === 'rotate') {
    const err = finite(meta.valMeanErrorDeg);
    if (err === null) return null;
    return {
      label: 'sai so goc',
      text: `${err.toFixed(2)}°`,
      passes: err <= ROTATE_MAX_MEAN_ERROR_DEG,
    };
  }
  const acc = finite(meta.valAccuracy);
  if (acc === null) return null;
  return {
    label: 'valAccuracy',
    text: formatAccuracy(acc),
    passes: acc >= OCR_MIN_VAL_ACCURACY,
  };
}

/**
 * Cac diem lech so voi hop dong ONNX.
 *
 * Model lech hop dong khong lam hong build cung khong lam hong test — no chi doan sai mot
 * cach am tham, va do la kieu loi dat nhat de tim. Chi ra o day de nguoi dung phat hien
 * ngay luc nhin bang, thay vi sau mot tuan thong ke sai.
 */
function contractIssues(model: ModelDto): string[] {
  if (model.solver !== 'ocr' || model.meta === null) return [];
  const meta = model.meta;
  const issues: string[] = [];
  if (meta.imgHeight !== OCR_CONTRACT.imgHeight || meta.imgWidth !== OCR_CONTRACT.imgWidth) {
    issues.push(
      `kich thuoc anh ${meta.imgHeight}x${meta.imgWidth} khac hop dong ${OCR_CONTRACT.imgHeight}x${OCR_CONTRACT.imgWidth}`,
    );
  }
  if (meta.timeSteps !== OCR_CONTRACT.timeSteps) {
    issues.push(`timeSteps ${meta.timeSteps} khac hop dong ${OCR_CONTRACT.timeSteps}`);
  }
  if (meta.inputName !== OCR_CONTRACT.inputName) {
    issues.push(`ten input "${meta.inputName}" khac hop dong "${OCR_CONTRACT.inputName}"`);
  }
  if (meta.outputName !== OCR_CONTRACT.outputName) {
    issues.push(`ten output "${meta.outputName}" khac hop dong "${OCR_CONTRACT.outputName}"`);
  }
  return issues;
}

// ---------------------------------------------------------------------------
// Sinh lenh Python
// ---------------------------------------------------------------------------

type Platform = 'windows' | 'posix';

/**
 * Duong dan trinh thong dich trong venv cua du an.
 * Khong dung `python` tran: may nao cung co vai ban Python, va ban dung la ban co torch.
 */
const PYTHON_PATH: Record<Platform, string> = {
  windows: 'training/.venv/Scripts/python.exe',
  posix: 'training/.venv/bin/python',
};

/**
 * Preset co san trong training/synth/presets/.
 * Danh sach nay soi guong cac file YAML trong repo; nguon su that van la lenh
 * `gen_text_captcha.py --list-presets`, nen o duoi co ghi chu nhac lai dieu do.
 */
const SYNTH_PRESETS = [
  'securimage',
  'phpbb',
  'kcaptcha',
  'php-gd',
  'mybb',
  'generic-hard',
  'all',
] as const;
type SynthPreset = (typeof SYNTH_PRESETS)[number];

/** Preset "all" tron nhieu bo sinh vao mot tap — dat ten thu muc theo y nghia, khong theo tham so. */
function synthDir(preset: SynthPreset): string {
  return preset === 'all' ? 'data/synth/mixed' : `data/synth/${preset}`;
}

/**
 * Dia chi server de go vao lenh curl.
 *
 * Uu tien VITE_CAPFORGE_API_BASE (dashboard co the duoc dat tren CDN, tro sang server khac),
 * neu khong co thi lay chinh dia chi dang mo — dung hon la hardcode localhost:8088, vi
 * nguoi dung co the dang xem dashboard tu may khac trong LAN.
 */
const rawApiBase: unknown = import.meta.env['VITE_CAPFORGE_API_BASE'];

function serverBaseUrl(): string {
  if (typeof rawApiBase === 'string' && rawApiBase.trim().length > 0) {
    return rawApiBase.trim().replace(/\/+$/, '');
  }
  return window.location.origin;
}

/**
 * Header xac thuc cho lenh curl, dung theo kieu dang dang nhap.
 *
 * Hai duong vao admin gui bang HAI header khac nhau (Authorization: Bearer cho API key,
 * X-Admin-Token cho token trong .env). Dua nham header thi lenh tra 401, va nguoi dung se
 * di tim loi o cho khac.
 */
function authHeaderParts(auth: AdminAuth | null, revealKey: boolean): { name: string; value: string } {
  if (auth !== null && auth.mode === 'adminToken') {
    return { name: 'X-Admin-Token', value: revealKey ? auth.key : '<admin token>' };
  }
  const key = auth !== null && revealKey ? auth.key : '<admin key>';
  return { name: 'Authorization', value: `Bearer ${key}` };
}

interface CommandInput {
  platform: Platform;
  preset: SynthPreset;
  count: number;
  modelName: string;
  profileKey: string;
  auth: AdminAuth | null;
  revealKey: boolean;
}

function genCommand(input: CommandInput): string {
  const py = PYTHON_PATH[input.platform];
  return `${py} training/synth/gen_text_captcha.py --preset ${input.preset} --count ${input.count} --out ${synthDir(input.preset)}`;
}

/**
 * Lenh xuat dataset that tu chinh capforge.
 *
 * Ban Windows dung Invoke-WebRequest chu khong phai curl: PowerShell 5.1 (ban mac dinh cua
 * Windows 10) lam hong dau nhay kep khi truyen chuoi JSON sang mot file .exe, nen lenh curl
 * chep tu tai lieu Linux se bao "invalid JSON" ma khong ai hieu vi sao. Invoke-WebRequest
 * nhan thang chuoi, khong qua tang bien dich dong lenh nao.
 */
function exportCommand(input: CommandInput): string {
  const base = serverBaseUrl();
  const header = authHeaderParts(input.auth, input.revealKey);
  const filter = JSON.stringify({
    taskType: 'ImageToTextTask',
    profileKey: input.profileKey,
  });

  if (input.platform === 'windows') {
    return [
      'Invoke-WebRequest -Method Post',
      `-Uri "${base}/admin/export"`,
      `-Headers @{ "${header.name}" = "${header.value}" }`,
      '-ContentType "application/json"',
      `-Body '${filter}'`,
      '-OutFile dataset.zip',
    ].join(' ');
  }
  return [
    `curl -X POST "${base}/admin/export"`,
    `-H "${header.name}: ${header.value}"`,
    '-H "Content-Type: application/json"',
    `-d '${filter}'`,
    '-o dataset.zip',
  ].join(' ');
}

function trainCommand(input: CommandInput): string {
  const py = PYTHON_PATH[input.platform];
  return `${py} training/train_ocr.py --data ${synthDir(input.preset)} --out data/models/${input.modelName}`;
}

/**
 * Duong dan de dien vao form Import.
 *
 * `train_ocr.py --out data/models/<ten>` ghi ra `<repo>/data/models/<ten>.onnx`, con registry
 * lai neo duong dan theo CAPFORGE_DATA_DIR (mac dinh chinh la `<repo>/data`). Vi vay o form
 * phai go `models/<ten>.onnx`, KHONG phai `data/models/<ten>.onnx` — day la cho de sai nhat
 * cua ca quy trinh, nen no duoc dien san thay vi de nguoi dung tu doan.
 */
function registryOnnxPath(modelName: string): string {
  return `models/${modelName}.onnx`;
}

// ---------------------------------------------------------------------------
// Trang
// ---------------------------------------------------------------------------

type ConfirmKind = 'delete' | 'activateMissingFile';

interface ConfirmState {
  kind: ConfirmKind;
  model: ModelDto;
}

export function Models() {
  const models = useAsync<ModelDto[]>(
    (signal) => adminFetch<ModelDto[]>('/admin/models', { signal }),
    [],
  );
  const profiles = useAsync<ProfileDto[]>(
    (signal) => adminFetch<ProfileDto[]>('/admin/profiles', { signal }),
    [],
  );
  const solvers = useAsync<SolverInfo[]>((signal) => api.solvers(signal), []);

  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // Tham so cua khoi lenh training.
  const [platform, setPlatform] = useState<Platform>(() =>
    // Doan he dieu hanh de lenh dan ra la lenh chay duoc ngay. Doan sai thi nguoi dung
    // bam mot nut la doi — re hon nhieu so voi luon bat ho tu chon.
    navigator.userAgent.includes('Windows') ? 'windows' : 'posix',
  );
  const [preset, setPreset] = useState<SynthPreset>('securimage');
  const [count, setCount] = useState(String(DEFAULT_SYNTH_COUNT));
  const [modelName, setModelName] = useState('ocr-v1');
  const [profileKey, setProfileKey] = useState('');
  const [revealKey, setRevealKey] = useState(false);

  const rows = models.data ?? [];
  const profileRows = profiles.data ?? [];

  const selected = useMemo(
    () => rows.find((row) => row.id === selectedId) ?? null,
    [rows, selectedId],
  );

  /** Model dang phuc vu cho tung cap (solver, profileKey) — de biet bam kich hoat la tien hay lui. */
  const servingByGroup = useMemo(() => {
    const map = new Map<string, ModelDto>();
    for (const row of rows) {
      if (row.active) map.set(groupKey(row), row);
    }
    return map;
  }, [rows]);

  /**
   * Model DANG PHUC VU ma file .onnx khong con tren dia.
   * Day la truong hop nghiem trong nhat cua trang nay: registry noi co model, nhung moi
   * request di qua no deu that bai roi lui ve fallback — dung ma khong ai biet.
   */
  const missingActive = useMemo(() => rows.filter((row) => row.active && !row.fileExists), [rows]);
  const missingInactive = useMemo(
    () => rows.filter((row) => !row.active && !row.fileExists),
    [rows],
  );

  // O nhap dang go do (rong, "0", "-5") khong duoc phep sinh ra mot lenh chay se loi:
  // lui ve mac dinh con hon dan ra mot lenh trong sach nhung sai.
  const parsedCount = Number.parseInt(count, 10);
  const safeCount =
    Number.isFinite(parsedCount) && parsedCount > 0 ? parsedCount : DEFAULT_SYNTH_COUNT;

  const commandInput: CommandInput = {
    platform,
    preset,
    count: safeCount,
    modelName: MODEL_NAME_PATTERN.test(modelName.trim()) ? modelName.trim() : 'ocr-v1',
    profileKey: profileKey.length > 0 ? profileKey : '<key>',
    auth: getAuth(),
    revealKey,
  };

  const runActivate = (model: ModelDto): void => {
    setBusyId(model.id);
    setActionError(null);
    setNotice(null);
    adminFetch<ModelDto>(`/admin/models/${model.id}/activate`, { method: 'POST' }).then(
      () => {
        setBusyId(null);
        setNotice(`Da kich hoat "${model.name}" v${model.version} cho solver ${model.solver}.`);
        models.reload();
      },
      (err: unknown) => {
        setBusyId(null);
        setActionError(errorMessage(err));
        // Van tai lai: 409 co the den tu mot thay doi ai do vua lam, va bang cu da lac hau.
        models.reload();
      },
    );
  };

  const runDelete = (model: ModelDto): void => {
    setBusyId(model.id);
    setActionError(null);
    setNotice(null);
    adminFetch<{ ok: boolean }>(`/admin/models/${model.id}`, { method: 'DELETE' }).then(
      () => {
        setBusyId(null);
        setNotice(
          `Da go "${model.name}" v${model.version} khoi registry. File .onnx VAN con tren dia.`,
        );
        if (selectedId === model.id) setSelectedId(null);
        models.reload();
      },
      (err: unknown) => {
        setBusyId(null);
        setActionError(errorMessage(err));
      },
    );
  };

  const onActivateClick = (model: ModelDto): void => {
    // Server khong kiem tra file luc kich hoat — no chi kiem tra do chinh xac. Neu file da
    // bien mat thi viec kich hoat van "thanh cong" roi that bai am tham o request dau tien.
    // Hoi lai mot cau o day re hon nhieu so voi di tim vi sao ti le fallback tang vot.
    if (!model.fileExists) {
      setConfirm({ kind: 'activateMissingFile', model });
      return;
    }
    runActivate(model);
  };

  const columns: Column<ModelDto>[] = [
    {
      key: 'model',
      header: 'Model',
      render: (row) => (
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="cf-mono font-medium text-slate-900 dark:text-slate-100">
              {row.name}
            </span>
            <Badge tone="neutral">v{row.version}</Badge>
            {row.active && <Badge tone="success">dang phuc vu</Badge>}
          </div>
          {row.meta !== null && row.meta.presets !== undefined && row.meta.presets.length > 0 && (
            <div className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
              preset: {row.meta.presets.join(', ')}
            </div>
          )}
          {row.meta === null && (
            <div className="mt-0.5 text-[11px] text-red-700 dark:text-red-400">
              metrics.json hong — khong doc duoc thong so
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'target',
      header: 'Solver / profile',
      render: (row) => (
        <div className="space-y-1">
          <Badge tone="info">{row.solver}</Badge>
          <div className="text-xs">{profileLabel(row.profileKey)}</div>
        </div>
      ),
    },
    {
      key: 'accuracy',
      header: 'Chất lượng',
      className: 'whitespace-nowrap',
      render: (row) => {
        const q = qualityCell(row);
        const real = finite(row.meta?.realAccuracy);
        return (
          <div className="text-xs leading-5">
            <div
              className={
                q === null
                  ? 'text-slate-400'
                  : q.passes
                    ? 'text-sm font-medium text-emerald-700 dark:text-emerald-400'
                    : 'text-sm font-medium text-amber-700 dark:text-amber-400'
              }
              title={
                row.solver === 'rotate'
                  ? `Nguong duoc phuc vu: sai so goc <= ${ROTATE_MAX_MEAN_ERROR_DEG}°`
                  : `Nguong duoc phuc vu: ${formatAccuracy(OCR_MIN_VAL_ACCURACY)}`
              }
            >
              {q === null ? 'chua do' : q.text}
            </div>
            <div className="text-slate-500 dark:text-slate-400">
              {q === null
                ? '—'
                : row.solver === 'rotate'
                  ? q.label
                  : real === null
                    ? 'chua do tren captcha that'
                    : `that: ${formatAccuracy(real)}`}
            </div>
          </div>
        );
      },
    },
    {
      key: 'samples',
      header: 'Dữ liệu train',
      className: 'whitespace-nowrap',
      render: (row) => {
        const train = finite(row.meta?.trainSamples);
        const val = finite(row.meta?.valSamples);
        const epochs = finite(row.meta?.epochs);
        if (train === null && val === null) {
          return <span className="text-slate-400">—</span>;
        }
        return (
          <div className="text-xs leading-5">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {formatNumber(train)} mau
            </div>
            <div className="text-slate-500 dark:text-slate-400">
              {formatNumber(val)} validation
              {epochs === null ? '' : ` · ${formatNumber(epochs)} epoch`}
            </div>
          </div>
        );
      },
    },
    {
      key: 'trained',
      header: 'Ngày train',
      className: 'whitespace-nowrap',
      render: (row) => {
        const trainedAt = row.meta?.trainedAt;
        // Khong co trainedAt thi hien ngay NAP vao registry, va noi ro do la ngay nap —
        // hai moc nay khac nhau, gop lam mot la bat dau bia du lieu.
        const iso = typeof trainedAt === 'string' && trainedAt.length > 0 ? trainedAt : null;
        return (
          <div className="text-xs leading-5">
            <div className="text-slate-700 dark:text-slate-300" title={formatDateTime(iso ?? row.createdAt)}>
              {formatRelative(iso ?? row.createdAt)}
            </div>
            <div className="text-slate-500 dark:text-slate-400">
              {iso === null ? 'ngay nap vao registry' : 'theo metrics.json'}
            </div>
          </div>
        );
      },
    },
    {
      key: 'file',
      header: 'File',
      render: (row) => (
        <div className="min-w-0 text-xs leading-5">
          <div className="cf-mono break-all text-slate-600 dark:text-slate-400">{row.onnxPath}</div>
          {row.fileExists ? (
            <div className="text-slate-500 dark:text-slate-400">{formatBytes(row.fileBytes)}</div>
          ) : (
            <div className="font-semibold text-red-700 dark:text-red-400">
              file khong con tren dia
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'whitespace-nowrap text-right',
      render: (row) => {
        const blocked = activationBlock(row.solver, row.meta);
        const serving = servingByGroup.get(groupKey(row)) ?? null;
        const isRollback = serving !== null && serving.id !== row.id && serving.version > row.version;
        const busy = busyId === row.id;

        return (
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={row.active ? 'cf-btn cf-btn-ghost' : 'cf-btn cf-btn-primary'}
                // Nut KHONG bao gio bi an khi model duoi nguong — chi bi vo hieu kem ly do
                // ngay ben duoi, de con so sanh cac lan train voi nhau (muc 12).
                disabled={row.active || blocked !== null || busy}
                title={
                  row.active
                    ? 'Đây là model đang phục vụ cho cặp solver/profile này'
                    : blocked !== null
                      ? `Khong kich hoat duoc: ${blocked}`
                      : isRollback
                        ? `Quay ve version nay, thay cho v${serving?.version ?? '?'} dang chay`
                        : 'Đưa model này lên phục vụ'
                }
                onClick={(event) => {
                  event.stopPropagation();
                  onActivateClick(row);
                }}
              >
                {busy ? (
                  <Spinner label="Đang đợi" />
                ) : row.active ? (
                  'Đang phục vụ'
                ) : isRollback ? (
                  `Rollback ve v${row.version}`
                ) : (
                  'Kích hoạt'
                )}
              </button>
              <button
                type="button"
                className="cf-btn cf-btn-danger"
                disabled={busy}
                title="Gỡ khỏi registry. Tệp .onnx trên đĩa KHÔNG bị xoá."
                onClick={(event) => {
                  event.stopPropagation();
                  setConfirm({ kind: 'delete', model: row });
                }}
              >
                Go
              </button>
            </div>
            {blocked !== null && (
              <p className="max-w-[22rem] text-right text-[11px] leading-4 text-amber-700 dark:text-amber-400">
                {blocked}
              </p>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Model</h1>
          <p className="max-w-3xl text-xs text-slate-500 dark:text-slate-400">
            Registry cac file <code className="cf-mono">.onnx</code> ma solver duoc phep dung.
            Mot cap (solver, profile) chi co dung mot model phuc vu tai mot thoi diem; kich
            hoat lai mot version cu chinh la rollback.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="cf-btn cf-btn-ghost" onClick={() => models.reload()}>
            Lam moi
          </button>
          <button
            type="button"
            className="cf-btn cf-btn-primary"
            onClick={() => {
              setActionError(null);
              setImportOpen(true);
            }}
          >
            Nap model
          </button>
        </div>
      </div>

      {missingActive.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
        >
          <p className="font-semibold">
            {missingActive.length} model dang phuc vu nhung file .onnx khong con tren dia
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {missingActive.map((row) => (
              <li key={row.id}>
                <span className="cf-mono">
                  {row.name} v{row.version}
                </span>{' '}
                — <span className="cf-mono">{row.onnxPath}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2">
            Solver se khong nap duoc model va tu lui ve fallback (guardrail 4): dich vu van
            chay, nhung khong con giai noi bo. Chep lai file vao dung duong dan, hoac kich
            hoat mot version khac o bang duoi.
          </p>
        </div>
      )}

      {missingInactive.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <p>
            {missingInactive.length} model trong registry khong con file tren dia (khong model
            nao trong so do dang phuc vu). Ban ghi van duoc giu de con so sanh cac lan train;
            muon don thi bam <span className="font-semibold">Go</span>.
          </p>
        </div>
      )}

      {actionError !== null && <ErrorBox message={actionError} title="Thao tác thất bại" />}

      {notice !== null && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {notice}
        </div>
      )}

      <section className="cf-card">
        <Table
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={models.loading}
          error={models.error}
          onRetry={models.reload}
          onRowClick={(row) => setSelectedId((prev) => (prev === row.id ? null : row.id))}
          isActive={(row) => row.id === selectedId}
          emptyMessage="Registry chưa có model nào"
          emptyHint="Chạy ba lệnh ở khối bên dưới để sinh dữ liệu, train, rồi bấm 'Nạp model'. Nếu bạn chắc đã nạp model ma bang van rong thi xem log server: registry tra danh sach rong khi khong doc duoc DB."
        />
      </section>

      {selected !== null && <ModelDetail model={selected} onClose={() => setSelectedId(null)} />}

      <TrainingCommands
        input={commandInput}
        profiles={profileRows}
        profilesError={profiles.error}
        preset={preset}
        onPreset={setPreset}
        count={count}
        onCount={setCount}
        modelName={modelName}
        onModelName={setModelName}
        profileKey={profileKey}
        onProfileKey={setProfileKey}
        platform={platform}
        onPlatform={setPlatform}
        revealKey={revealKey}
        onRevealKey={setRevealKey}
        onImport={() => {
          setActionError(null);
          setImportOpen(true);
        }}
      />

      {/*
        Dung mount co dieu kien chu khong chi truyen open=false: form lay gia tri mac dinh
        tu khoi lenh o tren (ten model, profile). Neu de component song mai thi lan mo thu hai
        van hien gia tri cua lan dau — dung luc nguoi dung vua doi ten model xong.
      */}
      {importOpen && (
        <ImportModelModal
          solvers={solvers.data}
          profiles={profileRows}
          defaultName={modelName}
          defaultProfileKey={profileKey}
          onClose={() => setImportOpen(false)}
          onDone={(message) => {
            setImportOpen(false);
            setNotice(message);
            models.reload();
          }}
          onFailedButMaybeCreated={() => {
            // Import co the that bai O BUOC KICH HOAT sau khi ban ghi da duoc tao (HTTP 409).
            // Tai lai bang thi nguoi dung nhin thay model moi ngay, thay vi tuong khong co gi
            // xay ra roi bam import lan hai va tao ra version trung lap.
            models.reload();
          }}
        />
      )}

      {confirm !== null && (
        <ConfirmModal
          state={confirm}
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            const target = confirm;
            setConfirm(null);
            if (target.kind === 'delete') runDelete(target.model);
            else runActivate(target.model);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Khung chi tiet mot model
// ---------------------------------------------------------------------------

interface ModelDetailProps {
  model: ModelDto;
  onClose: () => void;
}

function ModelDetail({ model, onClose }: ModelDetailProps) {
  const issues = contractIssues(model);
  const meta = model.meta;

  return (
    <section className="cf-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 className="text-sm font-semibold">
          Chi tiet <span className="cf-mono">{model.name}</span> v{model.version}
        </h2>
        <button type="button" className="cf-btn cf-btn-ghost" onClick={onClose}>
          Dong
        </button>
      </div>

      {issues.length > 0 && (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <p className="font-semibold">Model lệch hợp đồng ONNX của solver ocr</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
          <p className="mt-1">
            Lech hop dong khong lam hong build: model van nap duoc va van tra ket qua, chi la
            ket qua sai. Train lai bang <span className="cf-mono">training/train_ocr.py</span> de
            cac thong so tro ve dung chuan.
          </p>
        </div>
      )}

      {meta === null ? (
        <p className="mt-3 text-sm text-red-700 dark:text-red-400">
          Khong doc duoc metrics.json cua model nay. Nap lai model kem file metrics dung dinh
          dang thi cac thong so ben duoi moi hien.
        </p>
      ) : (
        <>
          <dl className="mt-3 grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
            {model.solver === 'rotate' ? (
              <>
                <DetailItem
                  label="sai số góc TB"
                  value={
                    finite(meta.valMeanErrorDeg) === null
                      ? 'chua do'
                      : `${(meta.valMeanErrorDeg as number).toFixed(2)}°`
                  }
                />
                <DetailItem
                  label="tỉ lệ ≤ 8°"
                  value={
                    finite(meta.valWithin8Deg) === null
                      ? 'chua do'
                      : formatAccuracy(meta.valWithin8Deg as number)
                  }
                />
                <DetailItem
                  label="anh dau vao"
                  value={`${meta.imgSize ?? '?'} x ${meta.imgSize ?? '?'} x ${meta.channels ?? '?'}`}
                />
                <DetailItem label="chuan hoa" value={meta.normalization ?? '?'} />
                <DetailItem
                  label="input / output"
                  value={`${meta.inputName ?? '?'} / ${meta.outputName ?? '?'}`}
                />
                <DetailItem
                  label="kho anh nguon"
                  value={meta.sourceImages ?? 'khong ghi'}
                  title={meta.sourceImages}
                />
              </>
            ) : (
              <>
                <DetailItem
                  label="charset"
                  value={meta.charset === undefined ? 'khong co' : `${meta.charset.length} ky tu`}
                  title={meta.charset}
                />
                <DetailItem label="anh dau vao" value={`${meta.imgHeight} x ${meta.imgWidth}`} />
                <DetailItem label="timeSteps" value={String(meta.timeSteps)} />
                <DetailItem label="chuan hoa" value={meta.normalization ?? '?'} />
                <DetailItem
                  label="input / output"
                  value={`${meta.inputName ?? '?'} / ${meta.outputName ?? '?'}`}
                />
                <DetailItem
                  label="mau anh"
                  value={meta.darkTextOnLight ? 'chu den tren nen trang' : 'chu trang tren nen den'}
                />
              </>
            )}
            <DetailItem label="nap vao registry" value={formatDateTime(model.createdAt)} />
            <DetailItem label="kich thuoc file" value={formatBytes(model.fileBytes)} />
          </dl>

          {typeof meta.notes === 'string' && meta.notes.length > 0 && (
            <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">{meta.notes}</p>
          )}

          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-slate-500 dark:text-slate-400">
              metrics.json day du
            </summary>
            <pre className="cf-mono mt-2 max-h-64 overflow-auto rounded bg-slate-100 p-2 text-[11px] dark:bg-slate-950">
              {prettyJson(meta)}
            </pre>
          </details>
        </>
      )}
    </section>
  );
}

interface DetailItemProps {
  label: string;
  value: string;
  title?: string;
}

function DetailItem({ label, value, title }: DetailItemProps) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="cf-mono truncate text-slate-800 dark:text-slate-200" title={title ?? value}>
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Khoi lenh training
// ---------------------------------------------------------------------------

interface TrainingCommandsProps {
  input: CommandInput;
  profiles: ProfileDto[];
  profilesError: string | null;
  preset: SynthPreset;
  onPreset: (value: SynthPreset) => void;
  count: string;
  onCount: (value: string) => void;
  modelName: string;
  onModelName: (value: string) => void;
  profileKey: string;
  onProfileKey: (value: string) => void;
  platform: Platform;
  onPlatform: (value: Platform) => void;
  revealKey: boolean;
  onRevealKey: (value: boolean) => void;
  onImport: () => void;
}

function TrainingCommands({
  input,
  profiles,
  profilesError,
  preset,
  onPreset,
  count,
  onCount,
  modelName,
  onModelName,
  profileKey,
  onProfileKey,
  platform,
  onPlatform,
  revealKey,
  onRevealKey,
  onImport,
}: TrainingCommandsProps) {
  const nameOk = MODEL_NAME_PATTERN.test(modelName.trim());
  const dataDirName = synthDir(preset);
  const realDir = profileKey.length > 0 ? `data/real/${profileKey}` : 'data/real/<key>';

  return (
    <section className="cf-card p-4">
      <h2 className="text-sm font-semibold">Lệnh cần chạy để train</h2>
      <p className="mt-1 max-w-4xl text-xs text-slate-500 dark:text-slate-400">
        Training chay <span className="font-semibold">ngoài Node</span>, bang Python trong{' '}
        <code className="cf-mono">training/.venv</code>. Ly do o muc 4 dac ta: duong giai captcha
        phai nhe va luon san sang nen inference chay thang trong Node bang{' '}
        <code className="cf-mono">onnxruntime-node</code>; con training thi chay tay, thinh thoang,
        va can PyTorch — dung service Python trong production chi de train mot thang mot lan la
        them mot thu co the sap. Ket qua cua Python la mot file{' '}
        <code className="cf-mono">.onnx</code> kem <code className="cf-mono">metrics.json</code>;
        do la tat ca nhung gi Node can biet.
      </p>
      <p className="mt-2 max-w-4xl text-xs text-slate-500 dark:text-slate-400">
        Chay cac lenh nay tu <span className="font-semibold">thu muc goc cua repo</span> (duong dan
        ben duoi la duong dan tuong doi).
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div>
          <label className="cf-label" htmlFor="cmd-platform">
            He dieu hanh
          </label>
          <select
            id="cmd-platform"
            className="cf-select"
            value={platform}
            onChange={(event) => onPlatform(event.target.value === 'windows' ? 'windows' : 'posix')}
          >
            <option value="windows">Windows (PowerShell)</option>
            <option value="posix">Linux / macOS</option>
          </select>
        </div>

        <div>
          <label className="cf-label" htmlFor="cmd-preset">
            Preset captcha
          </label>
          <select
            id="cmd-preset"
            className="cf-select"
            value={preset}
            onChange={(event) => {
              const next = SYNTH_PRESETS.find((item) => item === event.target.value);
              if (next !== undefined) onPreset(next);
            }}
          >
            {SYNTH_PRESETS.map((item) => (
              <option key={item} value={item}>
                {item === 'all' ? 'all — trộn mọi preset' : item}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="cf-label" htmlFor="cmd-count">
            So mau can sinh
          </label>
          <input
            id="cmd-count"
            className="cf-input cf-mono"
            type="number"
            min={100}
            step={1000}
            value={count}
            onChange={(event) => onCount(event.target.value)}
          />
        </div>

        <div>
          <label className="cf-label" htmlFor="cmd-model">
            Ten model
          </label>
          <input
            id="cmd-model"
            className="cf-input cf-mono"
            value={modelName}
            maxLength={120}
            onChange={(event) => onModelName(event.target.value)}
          />
          {!nameOk && (
            <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
              Chi cho phep chu, so va dau . _ - ; phai bat dau bang chu hoac so.
            </p>
          )}
        </div>

        <div>
          <label className="cf-label" htmlFor="cmd-profile">
            Profile
          </label>
          <select
            id="cmd-profile"
            className="cf-select"
            value={profileKey}
            onChange={(event) => onProfileKey(event.target.value)}
          >
            <option value="">— chua chon —</option>
            {profiles.map((item) => (
              <option key={item.key} value={item.key}>
                {item.key}
              </option>
            ))}
          </select>
          {profilesError !== null && (
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Khong tai duoc danh sach profile — lenh se giu cho{' '}
              <code className="cf-mono">&lt;key&gt;</code> de ban tu dien.
            </p>
          )}
        </div>
      </div>

      <ol className="mt-4 space-y-4">
        <CommandStep
          index={1}
          title="Sinh dữ liệu tổng hợp (miễn phí, không giới hạn)"
          command={genCommand(input)}
          note={
            <>
              Ket qua nam o <code className="cf-mono">{dataDirName}/</code> gom anh va{' '}
              <code className="cf-mono">labels.jsonl</code>. Day la nguon du lieu chinh (muc 10):
              dung lai chinh generator ma site dang dung thi co nhan dung tuyet doi, tuc thi, va
              khong ton mot dong nao. Xem danh sach preset day du bang{' '}
              <code className="cf-mono">--list-presets</code>.
            </>
          }
        />

        <CommandStep
          index={2}
          title="Hoặc / và: xuất dataset THẬT từ chính capforge"
          command={exportCommand(input)}
          note={
            <>
              Chi xuat mau da <span className="font-semibold">verified</span> (qua{' '}
              <code className="cf-mono">/reportCorrect</code> hoac gan nhan tay) hoac mau tong hop —
              nhan ban khong bao gio duoc dua vao train. Giai nen zip vao{' '}
              <code className="cf-mono">{realDir}/</code> roi them mot tham so{' '}
              <code className="cf-mono">--data {realDir}</code> nua o buoc 3: tron du lieu that vao
              du lieu tong hop la cach re nhat de keo do chinh xac tren captcha that len.
            </>
          }
          extra={
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={revealKey}
                onChange={(event) => onRevealKey(event.target.checked)}
              />
              Chen key that vao lenh (mac dinh de{' '}
              <code className="cf-mono">
                {input.auth !== null && input.auth.mode === 'adminToken'
                  ? '<admin token>'
                  : '<admin key>'}
              </code>{' '}
              de con chup man hinh hay dan lenh cho nguoi khac)
            </label>
          }
        />

        <CommandStep
          index={3}
          title="Train + xuat ONNX"
          command={trainCommand(input)}
          note={
            <>
              Sinh ra <code className="cf-mono">data/models/{input.modelName}.onnx</code>,{' '}
              <code className="cf-mono">.metrics.json</code> va{' '}
              <code className="cf-mono">.ckpt</code>. May nay khong co GPU nen train tren CPU:
              chay thu duong ong truoc bang{' '}
              <code className="cf-mono">--limit 200 --epochs 1</code> (vai phut) roi moi chay that.
              Script <span className="font-semibold">tu tu choi xuat</span> .onnx neu chua dat{' '}
              {formatAccuracy(OCR_MIN_VAL_ACCURACY)} tren validation.
            </>
          }
        />

        <CommandStep
          index={4}
          title="Nạp kết quả vào registry"
          note={
            <>
              Duong dan trong form tinh theo <code className="cf-mono">CAPFORGE_DATA_DIR</code> chu
              khong theo thu muc goc repo, nen o do phai go{' '}
              <code className="cf-mono">{registryOnnxPath(input.modelName)}</code> —{' '}
              <span className="font-semibold">khong</span> co tien to{' '}
              <code className="cf-mono">data/</code>. Form se dien san gia tri nay.
            </>
          }
          extra={
            <button type="button" className="cf-btn cf-btn-primary mt-2" onClick={onImport}>
              Mo form Import
            </button>
          }
        />
      </ol>
    </section>
  );
}

interface CommandStepProps {
  index: number;
  title: string;
  /** Bo trong cho buoc khong phai lenh dong lenh (vi du buoc bam nut tren dashboard). */
  command?: string;
  note?: ReactNode;
  extra?: ReactNode;
}

function CommandStep({ index, title, command, note, extra }: CommandStepProps) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-300 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-300">
        {index}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{title}</p>
        {command !== undefined && (
          <div className="mt-1 flex items-start gap-2">
            {/*
              Lenh de trong the <pre> co overflow rieng: lenh dai khong duoc phep lam ca trang
              cuon ngang, va cung khong duoc tu xuong dong — chep mot lenh bi ngat dong ve
              terminal la mot cach hay de chay nham.
            */}
            <pre className="cf-mono min-w-0 flex-1 overflow-x-auto rounded bg-slate-100 p-2 text-[12px] text-slate-800 dark:bg-slate-950 dark:text-slate-200">
              {command}
            </pre>
            <CopyButton value={command} />
          </div>
        )}
        {note !== undefined && (
          <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{note}</p>
        )}
        {extra}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Form Import
// ---------------------------------------------------------------------------

interface ImportModelModalProps {
  solvers: SolverInfo[] | null;
  profiles: ProfileDto[];
  defaultName: string;
  defaultProfileKey: string;
  onClose: () => void;
  onDone: (message: string) => void;
  onFailedButMaybeCreated: () => void;
}

function ImportModelModal({
  solvers,
  profiles,
  defaultName,
  defaultProfileKey,
  onClose,
  onDone,
  onFailedButMaybeCreated,
}: ImportModelModalProps) {
  const [name, setName] = useState(defaultName);
  const [solver, setSolver] = useState('ocr');
  const [profileKey, setProfileKey] = useState(defaultProfileKey);
  const [onnxPath, setOnnxPath] = useState(registryOnnxPath(defaultName));
  const [metricsPath, setMetricsPath] = useState('');
  const [activate, setActivate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Duong dan da bi sua tay thi khong tu ghi de nua — nguoi dung luon thang may.
  const [pathTouched, setPathTouched] = useState(false);

  const solverList = solvers ?? [];

  const submit = (): void => {
    const trimmedName = name.trim();
    if (!MODEL_NAME_PATTERN.test(trimmedName) || trimmedName.length > 120) {
      setError('Tên model chỉ cho phép chữ, số và dấu . _ - ; phải bắt đầu bằng chữ hoặc số.');
      return;
    }
    const trimmedSolver = solver.trim();
    if (trimmedSolver.length === 0) {
      setError('Phải chọn solver sẽ dùng model này.');
      return;
    }
    const trimmedPath = onnxPath.trim();
    if (trimmedPath.length === 0) {
      setError('Phải có đường dẫn tới tệp .onnx.');
      return;
    }

    setSaving(true);
    setError(null);
    adminFetch<ModelDto>('/admin/models/import', {
      method: 'POST',
      body: {
        name: trimmedName,
        solver: trimmedSolver,
        profileKey: profileKey.length > 0 ? profileKey : null,
        onnxPath: trimmedPath,
        metricsPath: metricsPath.trim().length > 0 ? metricsPath.trim() : null,
        activate,
      },
    }).then(
      (created) => {
        setSaving(false);
        onDone(
          created.active
            ? `Da nap "${created.name}" v${created.version} va dua len phuc vu.`
            : `Da nap "${created.name}" v${created.version} vao registry (chua kich hoat).`,
        );
      },
      (err: unknown) => {
        setSaving(false);
        setError(errorMessage(err));
        onFailedButMaybeCreated();
      },
    );
  };

  return (
    <Modal
      open
      title="Nạp model vào registry"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="cf-btn cf-btn-ghost" onClick={onClose}>
            Huy
          </button>
          <button type="button" className="cf-btn cf-btn-primary" onClick={submit} disabled={saving}>
            {saving ? <Spinner label="Đang nạp" /> : 'Nạp model'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Server chi ghi nhan duong dan, khong copy file. Duong dan phai nam trong{' '}
          <code className="cf-mono">CAPFORGE_DATA_DIR</code> (mac dinh la thu muc{' '}
          <code className="cf-mono">data/</code> cua repo) va duoc tinh tuong doi tu do.
        </p>

        <div>
          <label className="cf-label" htmlFor="import-name">
            Ten model
          </label>
          <input
            id="import-name"
            className="cf-input cf-mono"
            value={name}
            maxLength={120}
            placeholder="vd: ocr-securimage"
            onChange={(event) => {
              const next = event.target.value;
              setName(next);
              // Doi ten thi duong dan doi theo, tru khi nguoi dung da tu sua duong dan.
              if (!pathTouched) setOnnxPath(registryOnnxPath(next.trim()));
            }}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Cung mot ten thi moi lan nap la mot version moi, khong de len nhau — do la dieu kien
            de con duong rollback.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="cf-label" htmlFor="import-solver">
              Solver
            </label>
            {solverList.length > 0 ? (
              <select
                id="import-solver"
                className="cf-select"
                value={solver}
                onChange={(event) => setSolver(event.target.value)}
              >
                {/* Solver dang chay co the chua co ten dang can — van cho go tay o duoi. */}
                {solverList.some((item) => item.name === solver) ? null : (
                  <option value={solver}>{solver}</option>
                )}
                {solverList.map((item) => (
                  <option key={item.name} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="import-solver"
                className="cf-input cf-mono"
                value={solver}
                onChange={(event) => setSolver(event.target.value)}
              />
            )}
          </div>

          <div>
            <label className="cf-label" htmlFor="import-profile">
              Profile (tuy chon)
            </label>
            <select
              id="import-profile"
              className="cf-select"
              value={profileKey}
              onChange={(event) => setProfileKey(event.target.value)}
            >
              <option value="">generic — moi profile</option>
              {profiles.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.key}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              De trong thi model duoc dung cho moi profile chua co model rieng.
            </p>
          </div>
        </div>

        <div>
          <label className="cf-label" htmlFor="import-onnx">
            Duong dan file .onnx
          </label>
          <input
            id="import-onnx"
            className="cf-input cf-mono"
            value={onnxPath}
            maxLength={1024}
            placeholder="vd: models/ocr-v1.onnx"
            onChange={(event) => {
              setPathTouched(true);
              setOnnxPath(event.target.value);
            }}
          />
        </div>

        <div>
          <label className="cf-label" htmlFor="import-metrics">
            Duong dan metrics.json (tuy chon)
          </label>
          <input
            id="import-metrics"
            className="cf-input cf-mono"
            value={metricsPath}
            maxLength={1024}
            placeholder="để trống = tự tìm <ten>.metrics.json bên cạnh tệp .onnx"
            onChange={(event) => setMetricsPath(event.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Không có metrics hợp lệ thì model bị từ chối ngay từ đây: không biết charset và cách
            chuẩn hoá thì không thể giải mã đầu ra của nó.
          </p>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4"
            checked={activate}
            onChange={(event) => setActivate(event.target.checked)}
          />
          <span>
            Kích hoạt ngay sau khi nạp
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              Chi co tac dung khi valAccuracy dat {formatAccuracy(OCR_MIN_VAL_ACCURACY)}. Neu chua
              dat, model van duoc nap vao registry nhung khong len phuc vu — va ban se thay ly do
              o day.
            </span>
          </span>
        </label>

        {error !== null && <ErrorBox message={error} title="Nạp model thất bại" />}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Hop xac nhan
// ---------------------------------------------------------------------------

interface ConfirmModalProps {
  state: ConfirmState;
  onClose: () => void;
  onConfirm: () => void;
}

function ConfirmModal({ state, onClose, onConfirm }: ConfirmModalProps) {
  const { kind, model } = state;
  const isDelete = kind === 'delete';

  return (
    <Modal
      open
      title={isDelete ? 'Gỡ model khỏi registry?' : 'Kích hoạt model không còn tệp?'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="cf-btn cf-btn-ghost" onClick={onClose}>
            Huy
          </button>
          <button
            type="button"
            className={isDelete ? 'cf-btn cf-btn-danger' : 'cf-btn cf-btn-primary'}
            onClick={onConfirm}
          >
            {isDelete ? 'Gỡ khỏi registry' : 'Vẫn kích hoạt'}
          </button>
        </>
      }
    >
      <div className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
        <p>
          <span className="cf-mono font-medium">
            {model.name} v{model.version}
          </span>{' '}
          — solver <span className="cf-mono">{model.solver}</span>,{' '}
          {model.profileKey === null ? 'generic' : <span className="cf-mono">{model.profileKey}</span>}
        </p>
        {isDelete ? (
          <>
            <p>
              Chi xoa ban ghi trong registry. File{' '}
              <span className="cf-mono">{model.onnxPath}</span> tren dia{' '}
              <span className="font-semibold">khong bi dong toi</span> — cong train khong bao gio
              mat vi mot cu bam nham.
            </p>
            {model.active && (
              <p className="rounded border border-red-300 bg-red-50 p-2 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
                Model nay <span className="font-semibold">dang phuc vu</span>. Go xong thi cap
                (solver, profile) nay khong con model nao, va solver se lui ve fallback cho den khi
                ban kich hoat model khac.
              </p>
            )}
          </>
        ) : (
          <p className="rounded border border-red-300 bg-red-50 p-2 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            Tệp <span className="cf-mono">{model.onnxPath}</span> không còn trên đĩa. Kích hoạt
            xong thì registry sẽ nói là có model, nhưng mọi request đều thất bại ở bước nạp tệp
            rồi lui về fallback. Chỉ làm việc này nếu bạn sắp chép tệp vào đúng chỗ đó.
          </p>
        )}
      </div>
    </Modal>
  );
}
